import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import {
  IHighlight,
  useQueryHighlights,
} from 'lib/api/highlights/useQueryHighlights';
import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
  PaginationConstants,
} from 'lib/const/PaginationConstants';
import { HIGHLIGHTS_SORT_KEYS } from './constants';
import {
  ICreateHighlightParams,
  useCreateHighlightMutation,
} from 'lib/api/highlights/useCreateHighlightMutation';
import { useUpdateHighlightMutation } from 'lib/api/highlights/useUpdateHighlightMutation';
import { UpsertHighlightModal } from './modal/UpsertHighlightModal';
import { HighlightList } from './components/HighlightList';

export const Highlights = forwardRef((_, ref) => {
  // modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [highlightToUpsert, setHighlightToUpsert] = useState<IHighlight | null>(
    null
  );

  // query
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [highlightSortKey, setHighlightSortKey] =
    useState<HIGHLIGHTS_SORT_KEYS | null>(null);
  const [highlightSortOrder, setHighlightSortOrder] = useState(
    PaginationConstants.DESCENDING
  );

  const onPaginationChange = async ({
    page,
    size,
  }: {
    page: number;
    size: number;
  }) => {
    setPage(page);
    setSize(size);
  };

  const onSortingUpdate = (sortKey: string, order: PaginationConstants) => {
    setHighlightSortKey(sortKey as HIGHLIGHTS_SORT_KEYS);
    setHighlightSortOrder(order);
    setPage(DEFAULT_PAGE);
  };

  const params = {
    sortKey: highlightSortKey,
    order: highlightSortOrder || PaginationConstants.ASCENDING,
    start: page * size,
    limit: size,
  };

  const { data, isLoading: isLoadingHighlights } = useQueryHighlights(params);
  const highlights = data?.highlights || [];
  const count = data?.count || 0;

  const handleModalClose = () => {
    setIsModalOpen(false);
    setHighlightToUpsert(null);
  };

  const { mutateAsync: createHighlight, isLoading: isCreating } =
    useCreateHighlightMutation(handleModalClose);

  const { mutateAsync: updateHighlight, isLoading: isUpdating } =
    useUpdateHighlightMutation(handleModalClose);

  const handleSave = (data: ICreateHighlightParams) => {
    if (highlightToUpsert?.highlightId) {
      updateHighlight({ id: highlightToUpsert.highlightId, ...data });
    } else {
      createHighlight(data);
    }
  };

  const handleEditClick = (highlight: IHighlight) => {
    setHighlightToUpsert(highlight);
    setIsModalOpen(true);
  };

  const handleCreateClick = () => {
    setHighlightToUpsert(null);
    setIsModalOpen(true);
  };

  const openAddModal = useCallback(() => {
    handleCreateClick();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openAddModal,
    }),
    [openAddModal]
  );
  return (
    <>
      <HighlightList
        data={highlights || []}
        isLoadingHighlights={isLoadingHighlights}
        count={count}
        page={page}
        size={size}
        sortKey={highlightSortKey}
        order={highlightSortOrder}
        onPaginationChange={onPaginationChange}
        onSortingUpdate={onSortingUpdate}
        handleEditClick={handleEditClick}
        handleCreateClick={handleCreateClick}
      />
      {isModalOpen && (
        <UpsertHighlightModal
          onClose={handleModalClose}
          onSave={handleSave}
          highlight={highlightToUpsert}
          isLoading={isCreating || isUpdating}
        />
      )}
    </>
  );
});
